import React from 'react';
import styled from 'styled-components';
import { Tooltip, IconButton, ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, Avatar } from '@mic3/platform-ui';

import ExpansionCard from 'app/components/molecules/ExpansionCard/ExpansionCard';
import ListItem from 'app/components/molecules/List/ListItem';
import RolesSelect, { rolesMap } from 'app/components/molecules/RolesSelect/RolesSelect';
import Icon from 'app/components/atoms/Icon/Icon';

import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import { getStr } from 'app/utils/utils';
import { toUniqueUUID } from 'app/utils/string/string-utils';
import { get } from 'app/utils/lo/lo';

import CheckBoxBlank from 'assets/images/icons/check_box_outline_blank.png';
import CheckBox from 'assets/images/icons/check_box.png';

// Styled components shared across SelectionList variants
export const StyledExpansionCard = styled(ExpansionCard)`
    background: ${({ theme }) => theme.material.colors.background.default} !important;
    box-shadow: none !important;
    transition: none !important;
    margin-top: -16px !important;
`;

export const ListItemStyled = styled(ListItem)`
    ${({ disabled }) => (!disabled ? 'cursor: pointer;' : '')}
    width: 100%;
    max-width: 1024px;
    box-shadow: none;
    background: ${({ selected, theme }) => (selected ? theme.material.colors.background.active : theme.material.colors.background.paper)};
    &:hover {
        background: ${({ selected, theme }) => (selected ? theme.material.colors.background.active : theme.material.colors.background.hover)};
    }
    & h3 {
        display: flex;
        flex-direction: row;
        align-items: center;
    }
`;

export const ListItemChildStyled = styled(ListItemStyled)`
    background: none !important;
`;

export const StyledAvatar = styled(Avatar)`
    height: 40px;
    width: 40px;
    && {
        ${({ selected }) =>
    selected
        ? `
            background-color: gray !important;
            color: transparent
        `
        : `
            ${ListItemStyled}:hover & {
                color: transparent;
            }
        `};
    }
`;

export const StyledAvatarOverlay = styled.div`
    display: none;
    width: 24px;
    height: 24px;
    position: absolute;
    z-index: 1;
    left: 16px;
    top: 18px;
    && {
        ${({ selected }) =>
    selected
        ? `
            display: block;
            background-image: url(${CheckBox});
            background-repeat: no-repeat;
            background-position: center center;
        `
        : `
            ${ListItemStyled}:hover & {
                display: block;
                background-image: url(${CheckBoxBlank});
                background-repeat: no-repeat;
                background-position: center center;
            }
        `};
    }
`;

export const StyledAvatarChild = styled(Avatar)`
    height: 40px;
    width: 40px;
`;

export const ButtonWrapper = styled.div`
    & button {
        display: flex;
        width: 100%;
        & i:before {
            font-size: 18px !important;
        }
    }
    margin: 0 0 8px 0 !important;
`;

export const RolesWrapper = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
`;

export const DisabledReasonText = styled.div`
    display: flex;
    width: 100%;
    min-height: 48px;
    align-items: center;
    justify-content: flex-end;
    padding-right: 12px;
    font-size: 14px;
    color: ${({ theme }) => theme.base.disabled.textColor};
    white-space: nowrap;
    font-style: italic;
`;

export const ListStyled = styled.div`
    ${({ items, itemSize, fullHeight }) => `max-height: ${fullHeight ? window.innerHeight - 288 : items * itemSize + 70}px !important;`}
    position: relative;
    padding: 0;
    ${({ items, itemSize, maxHeight, fullHeight }) => {
    const height = items * itemSize + 70;
    return `height: ${fullHeight ? window.innerHeight - 288
        : height > maxHeight ? maxHeight : height}px !important;`;
}}
`;

export const ExpansionListItem = styled(ExpansionPanel)`
    background-color: ${({ selected }) => (selected ? 'rgba(0, 81, 255, 0.2)' : 'rgba(255, 255, 255, 0.04)')} !important;
    width: 100%;
    border-radius: 0;
    && {
        background-color: transparent;
        box-shadow: none;

        &::before {
            display: none !important;
        }
    }
`;

export const ExpansionSummary = styled(ExpansionPanelSummary)`
    padding: 0 !important;

    & .MuiExpansionPanelSummary-content {
        margin: 0 !important;
        width: 100%;

        & > div {
            background: transparent !important;
            margin-bottom: 0 !important;
        }

        & > div > div > div:nth-child(2) {
            padding-right: 48px;
        }
    }

    & .MuiExpansionPanelSummary-expandIcon {
        padding: 0 4px;
        margin-right: 15px;
        position: absolute;
        right: 46px;
    }
`;

export const ExpansionPanelDetailsStyled = styled(ExpansionPanelDetails)`
    width: 100%;
    flex-direction: column;
    & .MuiDivider-root {
        margin-bottom: 12px;
    }
    ${({ itemCount, itemSize }) => {
    const height = itemSize * itemCount + 105;
    return `height: ${height}px;`;
}}
`;

// Pure helpers shared across SelectionList variants
export function getSubTitle(type, data) {
    switch (type) {
        case 'class':
            return getStr(data, 'uri', '');
        case 'user':
            return `@${getStr(data, 'username', '')}`;
        default:
            return `#${toUniqueUUID(getStr(data, 'id', ''))}`;
    }
}

export function getAvatarUrl(type, id, image) {
    if (type === 'user') {
        return image && getAttachmentUrl(id, 'user', image);
    }
    if (!['workspace', 'team'].includes(type)) {
        return image;
    }
    return null;
}

export function computeHasLowerAccess(userRole, entityRole) {
    const rolesList = rolesMap?.[userRole];
    if (rolesList && entityRole) {
        return !rolesMap?.[userRole].includes(entityRole);
    }
    return false;
}

// Small reusable action renderer (optional usage)
export function renderListItemActions({ disabled, showRoles, setRole, userRole }, data, hasLowerAccess, removeOneFn) {
    const { disabledReason } = data || {};
    if (disabledReason === 'owner') {
        return <DisabledReasonText>Cannot edit owner</DisabledReasonText>;
    }
    if (disabledReason === 'assignee_self') {
        return <DisabledReasonText>Cannot edit own access</DisabledReasonText>;
    }
    if (disabledReason === 'assignee') {
        return <DisabledReasonText>Cannot edit assignee</DisabledReasonText>;
    }
    return (
        <RolesWrapper>
            {showRoles && (
                <RolesSelect
                    disabled={disabled}
                    setRole={r => setRole && setRole(r, data)}
                    defaultValue={data.role}
                    entityRole={userRole}
                />
            )}
            <Tooltip title={`Remove`} arrow>
                <IconButton
                    disabled={hasLowerAccess || disabled}
                    size="small"
                    onClick={e => (!disabled && !hasLowerAccess) && removeOneFn(e, get(data, 'id'))}
                >
                    <Icon type="mdi" name="close" size="md" />
                </IconButton>
            </Tooltip>
        </RolesWrapper>
    );
}


